import { useQuery, useMutation } from "@tanstack/react-query";
import type { ContactSubmission, ContactReply } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Mail, Eye, MessageSquare, Clock, CheckCircle, Loader2, Send, Reply, Trash2 } from "lucide-react";

export default function AdminContactSubmissions() {
  const { toast } = useToast();
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [replyText, setReplyText] = useState("");
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<ContactSubmission | null>(null);

  const { data: submissions, isLoading, error } = useQuery<ContactSubmission[]>({
    queryKey: ["/api/admin/contact-submissions"],
  });

  const { data: replies } = useQuery<ContactReply[]>({
    queryKey: [`/api/admin/contact-submissions/${selectedSubmission?.id}/replies`],
    enabled: !!selectedSubmission,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/admin/contact-submissions/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contact-submissions"] });
      toast({
        title: "Status Updated",
        description: "Submission status has been updated",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: async ({ id, message }: { id: string; message: string }) => {
      return apiRequest("POST", `/api/admin/contact-submissions/${id}/reply`, { message });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contact-submissions"] });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/contact-submissions/${selectedSubmission?.id}/replies`] });
      setReplyText("");
      setShowReplyForm(false);
      if (selectedSubmission) {
        setSelectedSubmission({ ...selectedSubmission, status: 'replied', repliedAt: new Date() as any });
      }
      toast({
        title: "Reply Sent",
        description: "Your reply has been emailed to the sender",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to send reply. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteSubmissionMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/contact-submissions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contact-submissions"] });
      setSubmissionToDelete(null);
      toast({
        title: "Submission Deleted",
        description: "The contact submission has been removed.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete submission. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleViewSubmission = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setShowReplyForm(false);
    setReplyText("");
    if (submission.status === 'new') {
      updateStatus.mutate({ id: submission.id, status: 'read' });
    }
  };

  const handleSendReply = () => {
    if (!selectedSubmission || !replyText.trim()) return;
    sendReplyMutation.mutate({ id: selectedSubmission.id, message: replyText.trim() });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="default" data-testid={`badge-status-new`}>New</Badge>;
      case 'read':
        return <Badge variant="secondary" data-testid={`badge-status-read`}>Read</Badge>;
      case 'replied':
        return <Badge variant="outline" className="text-green-600 border-green-600" data-testid={`badge-status-replied`}>Replied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          <div className="text-lg text-muted-foreground">Loading submissions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-destructive mb-2">Access Denied</div>
          <div className="text-sm text-muted-foreground">You do not have permission to access this page.</div>
        </div>
      </div>
    );
  }

  const newCount = submissions?.filter(s => s.status === 'new').length || 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-wide" data-testid="text-contact-submissions-title">
            Contact Submissions
          </h1>
          <p className="text-muted-foreground">
            Messages from the Contact Us form
            {newCount > 0 && (
              <Badge variant="destructive" className="ml-2" data-testid="badge-new-count">
                {newCount} new
              </Badge>
            )}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            All Submissions
          </CardTitle>
          <CardDescription>
            View and respond to contact form messages
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!submissions || submissions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No contact submissions yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow 
                    key={submission.id} 
                    className={submission.status === 'new' ? 'bg-blue-50 dark:bg-blue-950/20' : ''}
                    data-testid={`row-submission-${submission.id}`}
                  >
                    <TableCell>{getStatusBadge(submission.status)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{submission.name}</div>
                      <div className="text-sm text-muted-foreground">{submission.email}</div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{submission.subject}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(submission.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSubmission(submission)}
                          data-testid={`button-view-${submission.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleViewSubmission(submission);
                            setShowReplyForm(true);
                          }}
                          data-testid={`button-reply-${submission.id}`}
                        >
                          <Reply className="w-4 h-4 mr-1" />
                          Reply
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSubmissionToDelete(submission)}
                          data-testid={`button-delete-${submission.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedSubmission} onOpenChange={(open) => {
        if (!open) {
          setSelectedSubmission(null);
          setShowReplyForm(false);
          setReplyText("");
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {selectedSubmission?.subject}
            </DialogTitle>
            <DialogDescription>
              From {selectedSubmission?.name} ({selectedSubmission?.email})
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Received: {formatDate(selectedSubmission.createdAt)}
                </div>
                {selectedSubmission.readAt && (
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    Read: {formatDate(selectedSubmission.readAt)}
                  </div>
                )}
                {selectedSubmission.repliedAt && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    Replied: {formatDate(selectedSubmission.repliedAt)}
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-2 text-muted-foreground">Their message:</p>
                <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap" data-testid="text-message-content">
                  {selectedSubmission.message}
                </div>
              </div>

              {replies && replies.length > 0 && (
                <div>
                  <Separator className="my-2" />
                  <p className="text-sm font-medium mb-3 text-muted-foreground">Your replies:</p>
                  <div className="space-y-3">
                    {replies.map((reply) => (
                      <div key={reply.id} className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg" data-testid={`reply-${reply.id}`}>
                        <div className="whitespace-pre-wrap text-sm">{reply.replyMessage}</div>
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Send className="w-3 h-3" />
                          Sent {formatDate(reply.sentAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showReplyForm ? (
                <div>
                  <Separator className="my-2" />
                  <p className="text-sm font-medium mb-2">Write your reply:</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    This will be emailed to {selectedSubmission.name} at {selectedSubmission.email}
                  </p>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply here..."
                    className="min-h-[120px] mb-3"
                    data-testid="textarea-reply"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowReplyForm(false);
                        setReplyText("");
                      }}
                      data-testid="button-cancel-reply"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSendReply}
                      disabled={!replyText.trim() || sendReplyMutation.isPending}
                      data-testid="button-send-reply"
                    >
                      {sendReplyMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Reply
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedSubmission(null)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => setShowReplyForm(true)}
                    data-testid="button-reply-modal"
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!submissionToDelete} onOpenChange={(open) => { if (!open) setSubmissionToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Submission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the submission from <strong>{submissionToDelete?.name}</strong> ({submissionToDelete?.email})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submissionToDelete && deleteSubmissionMutation.mutate(submissionToDelete.id)}
              disabled={deleteSubmissionMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteSubmissionMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
